import { Card, Col, Row, Table, Tooltip } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';

import fileDownload from 'services/fileDownload';
import { historiePDFdownloadApi } from 'services/spravaEmitenta/amlzData';
import { EStavZadostAmlz, historickeZadostiOsobyC2 } from 'services/spravaEmitenta/historickeZadosti';
import vyjadreniCdcp from 'services/spravaEmitenta/vyjadreniCdcp';
import { IOsobyGetResp, Iosoba } from 'services/spravaEmitenta/zadostPravo';

import getLanguageObject from 'utils/getLanguageObject';

const OsobyZasHistory: React.FC = () => {
    const { language } = useContext(Context);
    const [zadosti, setZadosti] = useState<IOsobyGetResp>();
    const [errStatus, setErrStatus] = useState<number>();
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingTable, setLoadingTable] = useState<boolean>(false);
    const [offset, setOffset] = useState<number>(0);
    const [modalMessage, setModalMessage] = useState<string>('');

    const columns = [
        {
            title: <TranslateText>EMP_PODANE_ZADOSTI_DATUM_PODANI</TranslateText>,
            dataIndex: 'datum',
            key: 'datum',
            width: 120,
            render: (text: string) => dayjs(text).format(' DD.MM.YYYY')
        },
        {
            ellipsis: true,
            title: <TranslateText>JMENO</TranslateText>,
            key: 'jmeno',
            dataIndex: 'jmeno',
            render: (_a: string, row: Iosoba) => row.jmeno + ' ' + row.prijmeni
        },
        {
            ellipsis: true,
            title: <TranslateText>TELEFON</TranslateText>,
            key: 'telefon',
            dataIndex: 'telefon',
            width: 130
        },
        {
            ellipsis: true,
            title: <TranslateText>EMAIL</TranslateText>,
            key: 'email',
            dataIndex: 'email'
        },

        {
            ellipsis: {
                showTitle: false
            },
            title: <TranslateText>TYP_ZADOSTI_TITLE</TranslateText>,
            key: 'typzadosti',
            dataIndex: 'typzadosti',
            render: (typ: string) => (
                <>
                    <Tooltip
                        mouseEnterDelay={0.5}
                        overlayClassName="tableTooltip"
                        placement="bottomRight"
                        title={getLanguageObject(language)[`AMLZ_TYPZADOSTI_${typ}`]}
                    >
                        {getLanguageObject(language)[`AMLZ_TYPZADOSTI_${typ}`]}
                    </Tooltip>
                </>
            )
        },
        {
            ellipsis: {
                showTitle: false
            },
            title: <TranslateText>STAV_ZPRACOVANI</TranslateText>,
            key: 'stav',
            dataIndex: 'stav',
            render: (stav: string) => (
                <>
                    <Tooltip
                        mouseEnterDelay={0.5}
                        overlayClassName="tableTooltip"
                        placement="bottomRight"
                        title={getLanguageObject(language)[`STAVZADOSTI_SIMPLE_${stav}`]}
                    >
                        {getLanguageObject(language)[`STAVZADOSTI_SIMPLE_${stav}`]}
                    </Tooltip>
                </>
            )
        },
        {
            dataIndex: 'stav',
            key: 'stav',
            width: 78,
            render: (_: string, row: Iosoba, index: number) => (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-evenly',
                        flexGrow: 1
                    }}
                >
                    {row.stav === EStavZadostAmlz.HOTOVOIIS && (
                        <a
                            id={`historickeZadostiZobrazitBtn${index}`}
                            onClick={() => {
                                setLoadingTable(true);
                                historiePDFdownloadApi(row.idzadosti!).then(data => {
                                    fileDownload(
                                        data.redirect,
                                        `${getLanguageObject(language)['AMLZ_TYPZADOSTI_' + row.typzadosti]}.pdf`
                                    ).finally(() => setLoadingTable(false));
                                });
                            }}
                        >
                            <TranslateText>ZOBRAZIT</TranslateText>
                        </a>
                    )}
                    {row.stav === EStavZadostAmlz.KDOPL && (
                        <a
                            id={`historickeZadostiVyjadreniBtn${index}`}
                            onClick={() => {
                                setLoading(true);
                                vyjadreniCdcp(row.idzadosti!)
                                    .then(data => setModalMessage(data.vyjadreni))
                                    .finally(() => setLoading(false));
                            }}
                        >
                            <TranslateText>VYJADRENI</TranslateText>
                        </a>
                    )}
                </div>
            )
        }
    ];

    useEffect(() => {
        // nacitanie dat pri zmene strany v table
        setLoading(true);
        setErrStatus(0);
        // historickeZadostiApi(aktivniFirma?.ic ? aktivniFirma.ic : '', offset)
        historickeZadostiOsobyC2({
            offset: offset,
            limit: 10,
            order: 'id:desc'
        })
            .then(resp => {
                setZadosti(resp);
            })
            .catch((err: any) => {
                setErrStatus(err.response?.status);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset]);

    return (
        <>
            {errStatus ? (
                <Row
                    justify="center"
                    align="middle"
                    style={{
                        height: '100%'
                    }}
                >
                    <Col span={16} flex={'0 0 auto'}>
                        <LoadingWrapper loading={loading}>
                            {errStatus === 404 ? (
                                <Title text="CHYBA_AMLZ_NEVEDENO" level={3} />
                            ) : errStatus ? (
                                <Title text="SLUZBA_NEDOSTUPNA" level={3} />
                            ) : null}
                        </LoadingWrapper>
                    </Col>
                </Row>
            ) : (
                <LoadingWrapper loading={loadingTable}>
                    <Title level={3} text="AMLZ_HISTORIE" />
                    <Row gutter={8}>
                        <Col span={24}>
                            <Card className="dash-card">
                                <Table
                                    loading={loading}
                                    columns={columns}
                                    rowKey={record => record.id}
                                    dataSource={zadosti?.osoby}
                                    locale={{
                                        emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                                    }}
                                    pagination={{
                                        total: zadosti?.count,
                                        onChange: page => setOffset((page - 1) * 10),
                                        current: offset / 10 + 1,
                                        showSizeChanger: false
                                    }}
                                    rowClassName={(_a, i) => {
                                        const classes = [];
                                        if (i % 2) classes.push('tableRowGray');
                                        return classes.join(' ');
                                    }}
                                />
                            </Card>
                        </Col>
                    </Row>
                </LoadingWrapper>
            )}
            <Modal
                title={<TranslateText>{'VYJADRENI_CDCP'}</TranslateText>}
                open={!!modalMessage}
                onCancel={() => setModalMessage('')}
                afterClose={() => {
                    setModalMessage('');
                }}
                footer={
                    <Row className="button-row-modal">
                        <ButtonComponent
                            id="historickeZadostiZavritBtn"
                            buttontext="ZAVRIT"
                            onClick={() => {
                                setModalMessage('');
                            }}
                        />
                    </Row>
                }
            >
                <Row>
                    <Paragraph text={modalMessage} />
                </Row>
            </Modal>
        </>
    );
};

export default OsobyZasHistory;
